import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Stack } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import CustomButton from 'components/button/CustomButton';
import ProjectStatusChip from 'components/chip/ProjectStatusChip';
import Circular from 'components/common/CustomCircular';
import DetailPanel from 'components/condition-list/DetailPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleHeaderAction,
  ConsoleSearchFilter,
  ConsoleTable,
  buildInitialFiltersFromQuery,
} from 'components/console';
import {
  CsvBulkUploadDialog,
  CsvTemplateDownloadDialog,
  downloadCsvTemplate,
} from 'components/csv';
import { CsvBulkConfig } from 'components/csv/types';
import ConfirmDialog from 'components/dialog/ConfirmDialog';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import { Role } from 'stores/AuthenticationStoreAdapter';
import { ClientResult, listClient } from 'stores/ClientStoreAdapter';
import {
  MasterTypeResult,
  getDistribution,
  getIndustry,
  getProjectScale,
} from 'stores/MasterTypeStoreAdapter';
import {
  CreateProjectParams,
  ProjectDetailResult,
  ProjectListResult,
  ProjectStatus,
  TechStackItem,
  getProject,
  getTechStack,
  listProject,
  updateProject,
} from 'stores/ProjectStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { generateUUID } from 'utils/Common';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

import ProjectCreateForm, { ProjectCreateFormHandle } from './components/ProjectCreateForm';
import ProjectUpdateForm, { ProjectUpdateFormHandle } from './components/ProjectUpdateForm';
import { createProjectBulkConfig, createProjectCsvFields } from './components/projectCsvConfig';

type DrawerMode = 'none' | 'create' | 'detail';

const ProjectManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, managers } = useAuth();
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const [isProgress, setProgress] = useState(true);
  const [projects, setProjects] = useState<ProjectListResult[]>([]);
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectDetailResult | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('none');
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [closeEndDate, setCloseEndDate] = useState('');
  const [isCsvTemplateDialogOpen, setIsCsvTemplateDialogOpen] = useState(false);
  const [isCsvBulkDialogOpen, setIsCsvBulkDialogOpen] = useState(false);
  const [projectScales, setProjectScales] = useState<MasterTypeResult[]>([]);
  const [distributions, setDistributions] = useState<MasterTypeResult[]>([]);
  const [industries, setIndustries] = useState<MasterTypeResult[]>([]);
  const [techStackItems, setTechStackItems] = useState<TechStackItem[]>([]);
  // 現在適用中の検索パラメータ（作成/更新/CSV後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string>>({});

  const createFormRef = useRef<ProjectCreateFormHandle>(null);
  const updateFormRef = useRef<ProjectUpdateFormHandle>(null);

  const isDirectMode = !!routeProjectId;
  const isManager = userSession.roles.includes('MANAGER' as Role);
  const [searchParams] = useSearchParams();

  // クエリパラメータから初期検索パラメータを組み立てる（managerId=self は自分に解決）
  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (value === '') return;
      p[key] = key === 'managerId' && value === 'self' ? userSession.userId : value;
    });
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isDirectMode) {
      setProgress(true);
      getProject({ projectId: routeProjectId })
        .then((detail) => setSelectedProject(detail))
        .catch(() => errorDefault('プロジェクト情報の取得に失敗しました'))
        .finally(() => setProgress(false));
    } else {
      // 初期絞り込み: 課長デフォルト（managerId）＋ クエリパラメータ（クエリ優先）
      const initialParams: Record<string, string> = {};
      if (isManager) initialParams.managerId = userSession.userId;
      Object.assign(initialParams, queryParams);
      setActiveParams(initialParams);
      fetchProjects(initialParams);
      listClient({ includeExpired: false })
        .then(setClients)
        .catch(() => {});
      Promise.all([getProjectScale(), getDistribution(), getIndustry(), getTechStack()])
        .then(([scaleList, distList, indList, techData]) => {
          setProjectScales(scaleList);
          setDistributions(distList);
          setIndustries(indList);
          setTechStackItems([...(techData.TECH_LAYER || []), ...(techData.TECH_TYPE || [])]);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProjectId, queryParams]);

  const fetchProjects = (params: Record<string, string>) => {
    setProgress(true);
    listProject(params)
      .then((result: ProjectListResult[]) => {
        setProjects(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('プロジェクト情報の取得に失敗しました');
      });
  };

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchProjects(activeParams);

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelectedProject(null);
    setIsEditingProject(false);
  };

  // 作成・更新後は共通で「フォームを閉じて現在の検索条件で再検索」
  const handleCreated = () => {
    closeDrawer();
    refetch();
  };

  const handleProjectUpdated = () => {
    closeDrawer();
    refetch();
  };

  const handleCloseProject = () => {
    if (!selectedProject || !closeEndDate) return;
    setIsCloseDialogOpen(false);
    setProgress(true);
    updateProject({
      projectId: selectedProject.projectId,
      projectName: selectedProject.projectName,
      projectNameKana: selectedProject.projectNameKana,
      status: 'CLOSED',
      endDate: closeEndDate,
    })
      .then(() => {
        successDefault('プロジェクトを終了しました');
        handleProjectUpdated();
      })
      .catch((e: Error) => {
        errorDefault('プロジェクトの終了に失敗しました', e.message);
      })
      .finally(() => {
        setProgress(false);
        setCloseEndDate('');
      });
  };

  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchProjects(params);
  };

  const handleCreateClick = () => {
    setSelectedProject(null);
    setIsEditingProject(false);
    setDrawerMode('create');
  };

  const handleRowClick = (row: ProjectListResult) => {
    setIsEditingProject(false);
    setProgress(true);
    getProject({ projectId: row.projectId })
      .then((detail) => {
        setSelectedProject(detail);
        setDrawerMode('detail');
      })
      .catch(() => errorDefault('プロジェクト詳細の取得に失敗しました'))
      .finally(() => setProgress(false));
  };

  const managerInitialFilters: Filter[] = useMemo(() => {
    if (!isManager) return [];
    return [
      {
        id: generateUUID(),
        key: 'managerId',
        color: '#9DC08B',
        value: { key: userSession.userId, name: `課長: ${userSession.userName}` },
      },
    ];
  }, [isManager, userSession.userId, userSession.userName]);

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'projectName', name: 'プロジェクト名', type: 'text' as const, color: '#78C1F3' },
      {
        key: 'projectNameKana',
        name: 'プロジェクト名（カナ）',
        type: 'katakana-text' as const,
        color: '#3A98B9',
      },
      {
        key: 'status',
        name: 'ステータス',
        type: 'select' as const,
        color: '#B799FF',
        values: Object.entries(ProjectStatus).map(([key, label]) => ({ key, name: label })),
      },
      {
        key: 'clientId',
        name: '取引先',
        type: 'client-select-single' as const,
        color: '#FF8A65',
        clients,
      },
      {
        key: 'managerId',
        name: '課長',
        type: 'manager-select-single' as const,
        color: '#9DC08B',
        managers: managers as unknown as UserResult[],
      },
    ],
    [clients, managers],
  );

  // 検索バーに表示する初期フィルタ: 課長デフォルト ＋ クエリパラメータ由来（クエリ優先）
  const initialFilters: Filter[] = useMemo(() => {
    const queryFilters = buildInitialFiltersFromQuery(
      searchConditions,
      new URLSearchParams(queryParams),
    );
    const queryKeys = new Set(queryFilters.map((f) => f.key));
    const base = managerInitialFilters.filter((f) => !queryKeys.has(f.key));
    return [...base, ...queryFilters];
  }, [searchConditions, queryParams, managerInitialFilters]);

  const csvFields = useMemo(
    () =>
      createProjectCsvFields(
        managers,
        clients,
        projectScales,
        distributions,
        industries,
        techStackItems,
      ),
    [managers, clients, projectScales, distributions, industries, techStackItems],
  );
  const csvBulkConfig: CsvBulkConfig<CreateProjectParams> | null = useMemo(
    () =>
      managers.length > 0 && clients.length > 0
        ? createProjectBulkConfig(
            managers,
            clients,
            projectScales,
            distributions,
            industries,
            techStackItems,
          )
        : null,
    [managers, clients, projectScales, distributions, industries, techStackItems],
  );
  const handleCsvTemplateDownload = () => {
    downloadCsvTemplate(csvFields, 'プロジェクト一括登録テンプレート.csv');
  };

  const menuActions: ConsoleHeaderAction[] = [
    {
      label: 'CSV一括登録',
      icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvBulkDialogOpen(true),
    },
    {
      label: 'CSVテンプレートをダウンロード',
      icon: <FileDownloadIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvTemplateDialogOpen(true),
    },
  ];

  const columns: ConsoleColumn<ProjectListResult>[] = [
    {
      key: 'projectName',
      header: 'プロジェクト名',
      width: 260,
      ellipsis: (p) => p.projectName,
    },
    {
      key: 'clientName',
      header: '取引先',
      width: 180,
      ellipsis: (p) => p.clientName,
    },
    {
      key: 'managerName',
      header: '課長',
      width: 120,
      ellipsis: (p) => p.managerName,
    },
    {
      key: 'status',
      header: 'ステータス',
      width: 96,
      render: (p) => <ProjectStatusChip status={p.status} />,
    },
    {
      key: 'period',
      header: '期間',
      width: 170,
      render: (p) => (
        <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {p.startDate ? formatDate(p.startDate) : '—'}
          {p.endDate ? ` 〜 ${formatDate(p.endDate)}` : ''}
        </Box>
      ),
    },
  ];

  // ID指定モード（別ウィンドウ直開き）: 従来どおり DetailPanel 表示
  if (isDirectMode) {
    const directFooter =
      selectedProject && selectedProject.status !== 'CLOSED' && isEditingProject ? (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="更新"
            buttonVariant="primary"
            icon={<SaveIcon />}
            onClick={() => updateFormRef.current?.submit()}
          />
        </Stack>
      ) : undefined;

    return (
      <>
        {selectedProject ? (
          <DetailPanel footer={directFooter}>
            <ProjectUpdateForm
              ref={updateFormRef}
              project={selectedProject}
              isEditing={isEditingProject}
              onProjectUpdated={() => window.close()}
              onClose={() => window.close()}
              onEditingChange={setIsEditingProject}
              setProgress={setProgress}
            />
          </DetailPanel>
        ) : (
          <></>
        )}
        <Circular open={isProgress} />
      </>
    );
  }

  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<AddIcon />}
          onClick={() => createFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedProject && isEditingProject ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setIsEditingProject(false)}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={() => updateFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedProject && selectedProject.status !== 'CLOSED' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="プロジェクト終了"
          buttonVariant="success"
          icon={<StopIcon />}
          onClick={() => setIsCloseDialogOpen(true)}
        />
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={() => setIsEditingProject(true)}
        />
      </Stack>
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="プロジェクト"
        count={projects.length}
        description="案件の登録・稼働状況の管理・検索を行います"
        backLabel="要員管理"
        onBack={() => navigate('/member')}
        onRefresh={refetch}
        menuActions={menuActions}
        actions={
          <CustomButton
            label="プロジェクトを登録"
            buttonVariant="primary"
            icon={<AddIcon />}
            onClick={handleCreateClick}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={projects}
        rowKey={(p) => p.projectId}
        selectedKey={drawerMode === 'detail' ? selectedProject?.projectId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="プロジェクトがありません。「プロジェクトを登録」または「CSV一括登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="project-list"
            userProfileKey="member-project-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="プロジェクトの登録"
        width={720}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ProjectCreateForm
              ref={createFormRef}
              userSession={userSession}
              onCreate={handleCreated}
              onCancel={closeDrawer}
              setProgress={setProgress}
              hideHeader
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selectedProject !== null}
        onClose={closeDrawer}
        title={selectedProject?.projectName ?? ''}
        badges={selectedProject && <ProjectStatusChip status={selectedProject.status} />}
        width={720}
      >
        {selectedProject && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ProjectUpdateForm
                ref={updateFormRef}
                project={selectedProject}
                isEditing={isEditingProject}
                onProjectUpdated={handleProjectUpdated}
                onClose={closeDrawer}
                onEditingChange={setIsEditingProject}
                setProgress={setProgress}
                hideHeader
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      <ConfirmDialog
        open={isCloseDialogOpen}
        onClose={() => {
          setIsCloseDialogOpen(false);
          setCloseEndDate('');
        }}
        onConfirm={handleCloseProject}
        title="プロジェクト終了"
        confirmLabel="終了する"
        confirmColor="success"
        confirmDisabled={!closeEndDate}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          この操作は取り消せません。プロジェクトを終了すると編集できなくなります。
        </Alert>
        <FinancialYearMonthSelect
          dateType="end"
          value={closeEndDate}
          onChange={setCloseEndDate}
          label="終了日"
          required
        />
      </ConfirmDialog>

      <CsvTemplateDownloadDialog
        open={isCsvTemplateDialogOpen}
        onClose={() => setIsCsvTemplateDialogOpen(false)}
        entityName="プロジェクト"
        fields={csvFields}
        onDownload={handleCsvTemplateDownload}
      />

      {csvBulkConfig && (
        <CsvBulkUploadDialog
          open={isCsvBulkDialogOpen}
          onClose={() => setIsCsvBulkDialogOpen(false)}
          config={csvBulkConfig}
          onComplete={() => {
            closeDrawer();
            refetch();
          }}
        />
      )}

      <Circular open={isProgress} />
    </Box>
  );
};

export default ProjectManagementPage;
